import React, { useEffect, useState } from "react";
import { Grid, Paper, Box, Typography, MenuItem, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../shared/state/store";
import { ImplementationFacility } from "../models/implementation-facility";
import { RootState } from "../../../../../shared/state/root-reducer";
import { getClientFacilities, getClientFacility } from "../../../../state/facility/implementation-facility-thunk";
import { LoadingOverlay, TextField } from "@finpay-development/shared-components";
import EditIcon from '@mui/icons-material/Edit';
import { OperatingCostsModal } from "./operating-costs-modal";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AirlineSeatFlatIcon from '@mui/icons-material/AirlineSeatFlat';
import GroupsIcon from '@mui/icons-material/Groups';

function OperatingCosts() {

    const selectors = {
        currentClient: useSelector(
            (state: RootState) => state.implementationContext.implementationSpecialistClient?.currentClientEntityDetails
        ),
        facilities: useSelector(
            (state: RootState) => state.implementationContext.implementationFacility?.facilities
        ),
        isLoadingFacilities: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationFacility?.isLoading
        ),
        currentFacilityDetails: useSelector(
            (state: RootState) => state.implementationContext.implementationFacility?.currentFacilityDetails
        ),
    };

    const {
        facilities,
        isLoadingFacilities,
        currentClient,
        currentFacilityDetails
    } = selectors;

    const [selectedFacilityId, setSelectedFacilityId] = useState('');
    const [showEditOperatingCostsModal, setShowEditOperatingCostsModal] = useState(false);

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (currentClient.clientId > 0) {
            dispatch(getClientFacilities(currentClient.clientId))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (Number(selectedFacilityId) > 0) {
            dispatch(getClientFacility({ clientId: currentClient.clientId, facilityId: Number(selectedFacilityId) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFacilityId])

    const handleFacilityChange = (e: Event) => {
        const selectedFacId = (e.target as HTMLInputElement).value;
        setSelectedFacilityId(selectedFacId);
    };

    function handleEditOperatingCostsClose() {
        setShowEditOperatingCostsModal(false);
    }

    function handleEditCostsModalSubmit() {
        setShowEditOperatingCostsModal(false);
    }

    const handleBlur = () => { }

    function getGrossCostsPerUnit(bedtype: string): number {
        let retAmount: number = 0;
        if (bedtype === 'IP') {
            if (currentFacilityDetails?.bedCapacityIp !== undefined && currentFacilityDetails?.bedCapacityIp !== null && currentFacilityDetails?.bedCapacityIp !== 0) {
                if (currentFacilityDetails?.monthlyOperatingCostIp !== undefined) {
                    retAmount = Number(((currentFacilityDetails?.monthlyOperatingCostIp / currentFacilityDetails?.bedCapacityIp) / 30).toFixed(2));
                }
            }
        } else {
            if (currentFacilityDetails?.bedCapacityOp !== undefined && currentFacilityDetails?.bedCapacityOp !== null && currentFacilityDetails?.bedCapacityOp !== 0) {
                if (currentFacilityDetails?.monthlyOperatingCostOp !== undefined) {
                    retAmount = Number(((currentFacilityDetails?.monthlyOperatingCostOp / currentFacilityDetails?.bedCapacityOp) / 30).toFixed(2));
                }
            }
        }
        return retAmount;
    }

    return (
        <Paper>
            {isLoadingFacilities ? (
                <LoadingOverlay />
            ) : (
                <Box
                    style={{
                        margin: "0.5em",
                        padding: "1.0em",
                        borderBottom: "1px solid #cccccc",
                    }}
                >
                    <Grid container justifyContent="space-between" direction="row">
                        <Grid item justifyContent="center"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography variant="subtitle2">Facility Costs</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                select={true}
                                label="Facility"
                                name="facility"
                                value={selectedFacilityId}
                                loading={isLoadingFacilities}
                                onChange={handleFacilityChange}
                                onBlur={handleBlur}
                            >
                                {facilities
                                    .flat()
                                    .map((facility: ImplementationFacility) => (
                                        <MenuItem
                                            key={facility.facilityId}
                                            value={facility.facilityId}
                                        >
                                            {facility.facilityName}
                                        </MenuItem>
                                    ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    <Grid container direction="row">
                        <Grid item xs={12}>&nbsp;&nbsp;</Grid>
                    </Grid>
                    <br />
                    {Number(selectedFacilityId) > 0 && (
                        <Grid container direction="column">
                            <Grid container justifyContent="space-between">
                                <Grid item xs={4} >
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">In-Patient Capacity (# Beds)</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <AirlineSeatFlatIcon />&nbsp;&nbsp;&nbsp;{currentFacilityDetails?.bedCapacityIp}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">Monthly Operating Costs</Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <AttachMoneyIcon />&nbsp;&nbsp;&nbsp;{currentFacilityDetails?.monthlyOperatingCostIp}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={3}>
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">Gross Costs per Bed</Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <AttachMoneyIcon />&nbsp;&nbsp;&nbsp;{getGrossCostsPerUnit('IP')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton
                                        color="primary"
                                        onClick={() => setShowEditOperatingCostsModal(true)}
                                        className="edit-button"
                                        size="large"><EditIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Grid container justifyContent="space-between">
                                <Grid item xs={4} >
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">Out-Patient Capacity (# Patients)</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <GroupsIcon />&nbsp;&nbsp;&nbsp;{currentFacilityDetails?.bedCapacityOp}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">Monthly Operating Costs</Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <AttachMoneyIcon />&nbsp;&nbsp;&nbsp;{currentFacilityDetails?.monthlyOperatingCostOp}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="body2">Gross Costs per Patient</Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="subtitle2" className="mb-6">
                                                <AttachMoneyIcon />&nbsp;&nbsp;&nbsp;{getGrossCostsPerUnit('OP')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {showEditOperatingCostsModal && (
                        <OperatingCostsModal
                            open={showEditOperatingCostsModal}
                            facilityItem={currentFacilityDetails}
                            handleOperatingCostsModalCancel={handleEditOperatingCostsClose}
                            handleOperatingCostsModalSubmit={handleEditCostsModalSubmit}
                        />
                    )}
                </Box>
            )
            }
        </Paper>
    );
}

export default OperatingCosts;